import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { SyncPhaseReport } from '@/services/sync/sync-engine';
import type { SyncPauseReason, SyncPhase } from '@/types/offline-attendance';

/** The queue figures the orchestrator publishes with every change. */
export interface SyncCounts {
  /** Unsettled punches of the current scope that are not waiting for review (pending + in flight). */
  pendingCount: number;
  /**
   * Punches that need HR — what the badge counts: unsettled punches held behind
   * a refused one, plus punches ERPat refused within the last 7 days
   * (`OFFLINE_ACCEPTED_PRUNE_MS`, plan amendment C18). Older refusals stay on
   * the device and on the Sync page (history) but no longer raise the badge.
   */
  reviewCount: number;
  /** When the oldest unsettled punch was saved (device ms), or null. */
  oldestPendingAt: number | null;
}

/**
 * Offline-attendance sync diagnostics (`specs/offline-attendance-v1/implementation-plan.md`,
 * task FE-9) — what the sync badge, the banner and the Sync page read.
 *
 * Written ONLY by the orchestrator (`services/offline/offline-attendance.ts`):
 * `setPhase()` from every sync-engine phase report, `setCounts()` whenever the
 * scope's chain is re-read. Views never mutate it. Everything here describes
 * the CURRENT session's scope; `reset()` runs at sign-out (the queue itself
 * stays on the device — only this view of it is forgotten).
 */
export const useSyncStore = defineStore('sync', () => {
  const phase = ref<SyncPhase>('idle');
  const pauseReason = ref<SyncPauseReason | null>(null);
  /** When the queue head may be sent next (device ms), or null. */
  const nextAttemptAt = ref<number | null>(null);
  /** The last time an upload settled at least one punch (device ms), or null. */
  const lastSuccessAt = ref<number | null>(null);
  /** The result code the queue head waits on, or null. */
  const lastErrorCode = ref<string | null>(null);
  const pendingCount = ref(0);
  const reviewCount = ref(0);
  const oldestPendingAt = ref<number | null>(null);

  /** Mirror one sync-engine phase report. */
  function setPhase(report: SyncPhaseReport): void {
    phase.value = report.phase;
    pauseReason.value = report.pauseReason;
    nextAttemptAt.value = report.nextAttemptAt;
    lastSuccessAt.value = report.lastSuccessAt;
    lastErrorCode.value = report.lastErrorCode;
  }

  /** Mirror the queue figures (never negative; a non-number reads as 0). */
  function setCounts(counts: SyncCounts): void {
    pendingCount.value = wholeCount(counts.pendingCount);
    reviewCount.value = wholeCount(counts.reviewCount);
    oldestPendingAt.value = counts.oldestPendingAt;
  }

  function reset(): void {
    phase.value = 'idle';
    pauseReason.value = null;
    nextAttemptAt.value = null;
    lastSuccessAt.value = null;
    lastErrorCode.value = null;
    pendingCount.value = 0;
    reviewCount.value = 0;
    oldestPendingAt.value = null;
  }

  return {
    phase,
    pauseReason,
    nextAttemptAt,
    lastSuccessAt,
    lastErrorCode,
    pendingCount,
    reviewCount,
    oldestPendingAt,
    setPhase,
    setCounts,
    reset,
  };
});

function wholeCount(value: number): number {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}
